import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { libellesTraduits } from '../../i18n/libelles';
import discussionService, { marqueurPour } from '../../services/discussionService';
import { marqueurDossier } from '../../pages/conversations/utils';
import { chercherDossiers } from '../../pages/conversations/liensSinistre';
import { CHARTE_COLORS as C } from '../../variables/constants';

/**
 * Zone de saisie d'un message interne, avec menu de mention à la frappe du `@`.
 *
 * CE QUI FAIT LA VALEUR DE CE COMPOSANT N'EST PAS LA RECHERCHE, C'EST LE CLASSEMENT.
 * Le serveur renvoie les propositions triées par proximité réelle au dossier - le
 * rédacteur affecté, puis ceux qui ont déjà écrit dans le fil, puis l'organisme.
 * Déverser deux cents agents par ordre alphabétique obligerait à chercher un nom
 * que l'on connaît déjà. Ici le bon nom est presque toujours en tête, et la frappe
 * ne sert qu'aux cas restants.
 *
 * Le marqueur inséré porte l'UUID : le libellé visible n'est qu'un affichage, et le
 * serveur le réécrit à partir de l'identité réelle avant d'enregistrer.
 */

const { TextArea } = Input;

/** Le jeton en cours de frappe s'arrête au premier espace : un `@` isolé ouvre tout. */
const MOTIF_JETON = /@([\p{L}\p{N}_'’.-]*)$/u;
/**
 * `#` en début de mot cite un dossier (numéro, assuré, immatriculation), comme dans
 * les conversations. La barre d'un numéro (2026/0363) fait partie du jeton.
 */
const MOTIF_DOSSIER = /(?:^|\s)#([\p{L}\p{N}_'’./-]*)$/u;
const MIN_RECHERCHE_DOSSIER = 2;

const DELAI_FRAPPE = 180;

const TITRES_GROUPE = libellesTraduits('commun:saisieMention.groupes');

export default function SaisieMention({
  entiteType,
  entiteId,
  valeur,
  onChange,
  onEnvoyer,
  placeholder,
  disabled,
  rows = 3,
  autoFocus,
}) {
  const { t } = useTranslation('commun');
  const [ouvert, setOuvert] = useState(false);
  const [propositions, setPropositions] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [index, setIndex] = useState(0);
  const [debutJeton, setDebutJeton] = useState(null);
  const [mode, setMode] = useState('mention'); // 'mention' | 'dossier'

  const refZone = useRef(null);
  const refRecherche = useRef(null);
  const refRequete = useRef(0);

  const zone = () => refZone.current?.resizableTextArea?.textArea;

  const fermer = useCallback(() => {
    setOuvert(false);
    setPropositions([]);
    setDebutJeton(null);
  }, []);

  useEffect(() => () => clearTimeout(refRecherche.current), []);

  /**
   * Le menu s'ouvre à partir de la position du curseur et non de la fin du texte :
   * on corrige souvent une phrase au milieu d'un message déjà écrit, et un menu qui
   * ne réagirait qu'en fin de ligne obligerait à tout retaper.
   */
  const analyser = useCallback((texte, curseur) => {
    const avant = texte.slice(0, curseur);
    const dossier = MOTIF_DOSSIER.exec(avant);
    if (dossier) {
      const jeton = dossier[1];
      setDebutJeton(curseur - jeton.length - 1);
      setMode('dossier');
      setOuvert(true);
      setIndex(0);
      clearTimeout(refRecherche.current);
      if (jeton.length < MIN_RECHERCHE_DOSSIER) {
        setPropositions([]);
        setChargement(false);
        return;
      }
      setChargement(true);
      const requete = ++refRequete.current;
      refRecherche.current = setTimeout(async () => {
        const liste = await chercherDossiers(jeton, 8);
        // Une réponse lente ne doit pas écraser celle d'une frappe plus récente :
        // « 03 » effacerait les résultats de « 0363 ».
        if (requete !== refRequete.current) return;
        setPropositions(liste);
        setChargement(false);
      }, DELAI_FRAPPE);
      return;
    }
    const trouve = MOTIF_JETON.exec(avant);
    if (!trouve) {
      fermer();
      return;
    }
    const debut = curseur - trouve[0].length;
    setDebutJeton(debut);
    setMode('mention');
    setOuvert(true);
    setIndex(0);
    setChargement(true);

    clearTimeout(refRecherche.current);
    refRecherche.current = setTimeout(async () => {
      try {
        const liste = await discussionService.mentionnables(entiteType, entiteId, trouve[1]);
        setPropositions(liste);
      } catch {
        // Un menu vide vaut mieux qu'une alerte : l'agent peut continuer d'écrire.
        setPropositions([]);
      } finally {
        setChargement(false);
      }
    }, DELAI_FRAPPE);
  }, [entiteType, entiteId, fermer]);

  const surChangement = (e) => {
    const texte = e.target.value;
    onChange(texte);
    analyser(texte, e.target.selectionStart ?? texte.length);
  };

  const choisir = (proposition) => {
    if (debutJeton == null) return;
    const el = zone();
    const curseur = el?.selectionStart ?? valeur.length;
    const marqueur = mode === 'dossier' ? marqueurDossier(proposition) : marqueurPour(proposition);
    const nouveau = valeur.slice(0, debutJeton) + marqueur + ' ' + valeur.slice(curseur);
    onChange(nouveau);
    fermer();
    // Le curseur est replacé après l'insertion, sinon il retombe en fin de texte et
    // l'agent qui corrigeait le milieu de sa phrase se retrouve à la fin.
    const position = debutJeton + marqueur.length + 1;
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(position, position);
      }
    });
  };

  const surTouche = (e) => {
    if (ouvert && propositions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIndex((i) => (i + 1) % propositions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIndex((i) => (i - 1 + propositions.length) % propositions.length);
        return;
      }
      // Entrée valide la proposition tant que le menu est ouvert : c'est le geste
      // attendu, et il n'entre pas en conflit avec l'envoi, qui demande Ctrl.
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        choisir(propositions[index]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        fermer();
        return;
      }
    }
    // Ctrl/Cmd + Entrée envoie ; Entrée seul saute une ligne. Un message peut
    // solliciter trois personnes et part sans rappel : l'envoi ne doit pas être à
    // une touche d'écart d'un retour à la ligne.
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!disabled && valeur.trim()) onEnvoyer?.();
    }
  };

  let groupePrecedent = null;

  return (
    <div style={{ position: 'relative' }}>
      {ouvert && (
        <div
          style={{
            position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 6,
            background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto', zIndex: 30,
          }}
        >
          {chargement && propositions.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>
          )}
          {!chargement && propositions.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: C.faint }}>
              {mode === 'dossier' ? t('saisieMention.tapezDossier') : t('saisieMention.personneNeCorrespond')}
            </div>
          )}
          {mode === 'dossier' && propositions.map((d, i) => (
            <div
              key={d.sinistreTrackingId}
              onMouseDown={(e) => { e.preventDefault(); choisir(d); }}
              onMouseEnter={() => setIndex(i)}
              style={{
                padding: '7px 12px', cursor: 'pointer',
                background: i === index ? C.greenLight : '#fff',
              }}
            >
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text }}>
                {d.numeroSinistreLocal}
              </span>
              <span style={{
                display: 'block', fontSize: 11, color: C.muted,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {[d.assureNomComplet, d.assureImmatriculation, d.statut].filter(Boolean).join(' · ')}
              </span>
            </div>
          ))}
          {mode === 'mention' && propositions.map((p, i) => {
            const nouveauGroupe = p.groupe !== groupePrecedent;
            groupePrecedent = p.groupe;
            return (
              <React.Fragment key={`${p.cible}-${p.utilisateurTrackingId || p.libelle}`}>
                {nouveauGroupe && (
                  <div style={{
                    padding: '6px 12px 4px', fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                    textTransform: 'uppercase', color: C.faint, background: C.bg,
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    {TITRES_GROUPE[p.groupe] || p.groupe}
                  </div>
                )}
                <div
                  onMouseDown={(e) => { e.preventDefault(); choisir(p); }}
                  onMouseEnter={() => setIndex(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '7px 12px',
                    cursor: 'pointer', background: i === index ? C.greenLight : '#fff',
                  }}
                >
                  <span style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: p.cible === 'UTILISATEUR' ? C.greenMid : '#e5e7eb',
                    color: p.cible === 'UTILISATEUR' ? '#fff' : C.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {p.initiales}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{
                      display: 'block', fontSize: 13, fontWeight: 600, color: C.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {p.libelle}
                    </span>
                    {p.raison && (
                      <span style={{
                        display: 'block', fontSize: 11, color: C.muted,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {p.raison}
                      </span>
                    )}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <TextArea
        ref={refZone}
        rows={rows}
        value={valeur}
        autoFocus={autoFocus}
        onChange={surChangement}
        onKeyDown={surTouche}
        onBlur={fermer}
        onClick={(e) => analyser(valeur, e.target.selectionStart ?? 0)}
        disabled={disabled}
        maxLength={5000}
        placeholder={placeholder}
        style={{ resize: 'none' }}
      />
    </div>
  );
}
